import { cn, formatCurrency } from '@/lib/utils'
import { ShieldCheck, AlertTriangle, TrendingUp, Award } from 'lucide-react'

interface CreditDecisionProps {
  recommendedLimit: number
  maxLimit: number
  riskScore: number
  riskCategory: string
  confidence: 'high' | 'medium' | 'low'
  approvalStatus: 'approved' | 'conditional' | 'declined'
  conditions?: string[]
  className?: string
}

const statusConfig = {
  approved: {
    bg: 'bg-gradient-to-br from-risk-excellent to-green-600',
    icon: ShieldCheck,
    label: 'APPROVED',
    textColor: 'text-white',
  },
  conditional: {
    bg: 'bg-gradient-to-br from-risk-fair to-yellow-600',
    icon: AlertTriangle,
    label: 'CONDITIONAL',
    textColor: 'text-white',
  },
  declined: {
    bg: 'bg-gradient-to-br from-risk-critical to-red-700',
    icon: AlertTriangle,
    label: 'DECLINED',
    textColor: 'text-white',
  },
}

const confidenceColors = {
  high: 'text-risk-excellent',
  medium: 'text-risk-fair',
  low: 'text-risk-poor',
}

export function CreditDecision({
  recommendedLimit,
  maxLimit,
  riskScore,
  riskCategory,
  confidence,
  approvalStatus,
  conditions = [],
  className,
}: CreditDecisionProps) {
  const config = statusConfig[approvalStatus]
  const Icon = config.icon
  const utilizationPercent = (recommendedLimit / maxLimit) * 100

  return (
    <div className={cn('rounded-xl overflow-hidden shadow-lg', className)}>
      {/* Header with Status - Compact */}
      <div className={cn('p-3', config.bg)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-white/80 text-[10px] font-medium uppercase tracking-wider">
                Credit Decision
              </div>
              <div className="text-white text-lg font-bold">{config.label}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-white/80 text-[10px]">Risk Score</div>
            <div className="text-white text-xl font-bold">{Math.round(riskScore)}/10</div>
            <div className="text-white/70 text-[10px]">{riskCategory}</div>
          </div>
        </div>
      </div>

      {/* Main Content - Compact */}
      <div className="bg-white p-4">
        {/* Recommended Credit Limit */}
        <div className="text-center mb-3">
          <div className="text-gray-500 text-xs mb-0.5">Recommended Credit Limit</div>
          <div className="text-3xl font-bold text-dashboard-navy">
            {formatCurrency(recommendedLimit)}
          </div>
          <div className="flex items-center justify-center gap-2 mt-1">
            <div className={cn('text-xs font-medium', confidenceColors[confidence])}>
              {confidence.toUpperCase()} CONFIDENCE
            </div>
            <span className="text-gray-300">•</span>
            <div className="text-xs text-gray-500">
              of {formatCurrency(maxLimit)} max eligible
            </div>
          </div>
        </div>

        {/* Utilization Bar */}
        <div className="mb-3">
          <div className="flex justify-between text-[10px] text-gray-500 mb-1">
            <span>Credit Utilization</span>
            <span>{utilizationPercent.toFixed(0)}% of max</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                approvalStatus === 'approved'
                  ? 'bg-gradient-to-r from-risk-excellent to-green-400'
                  : approvalStatus === 'conditional'
                  ? 'bg-gradient-to-r from-risk-fair to-yellow-400'
                  : 'bg-gradient-to-r from-risk-critical to-red-400'
              )}
              style={{ width: `${utilizationPercent}%` }}
            />
          </div>
        </div>

        {/* Quick Stats - Inline */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <Award className="h-4 w-4 mx-auto text-dashboard-purple mb-0.5" />
            <div className="text-[10px] text-gray-500">Max Ticket</div>
            <div className="font-semibold text-xs">{formatCurrency(maxLimit)}</div>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <TrendingUp className="h-4 w-4 mx-auto text-dashboard-teal mb-0.5" />
            <div className="text-[10px] text-gray-500">Shadow Limit</div>
            <div className="font-semibold text-xs">{formatCurrency(recommendedLimit * 0.1)}</div>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <ShieldCheck className="h-4 w-4 mx-auto text-risk-excellent mb-0.5" />
            <div className="text-[10px] text-gray-500">Confidence</div>
            <div className={cn('font-semibold text-xs', confidenceColors[confidence])}>
              {confidence.charAt(0).toUpperCase() + confidence.slice(1)}
            </div>
          </div>
        </div>

        {/* Conditions if any - Compact */}
        {conditions.length > 0 && (
          <div className="border-t pt-2">
            <div className="text-[10px] font-medium text-gray-600 mb-1">Conditions:</div>
            <ul className="space-y-0.5">
              {conditions.map((condition, idx) => (
                <li key={idx} className="text-[10px] text-gray-500 flex items-start gap-1.5">
                  <span className="text-risk-fair">•</span>
                  {condition}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
